import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Step } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdminDetails } from "@/v2/stores";
import { getTourSteps, type TourStep, toJoyrideSteps } from "./tourSteps";

interface TourContextValue {
  isTourRunning: boolean;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  startTour: () => void;
  endTour: () => void;
  hasSeenTour: boolean;
  steps: Step[];
  tourSteps: TourStep[];
  isNavigating: boolean;
  navigateToStep: (stepIndex: number) => Promise<boolean>;
  currentRoute: string;
}

const TourContext = createContext<TourContextValue | null>(null);

const getTourStorageKey = (userId: string | undefined) =>
  userId ? `v2_tour_completed_${userId}` : "v2_tour_completed";

const DEFAULT_WAIT_TIMEOUT = 3000;

/**
 * Wait for an element to exist in the DOM
 */
const waitForElement = (
  selector: string,
  timeout: number = DEFAULT_WAIT_TIMEOUT
): Promise<Element | null> => {
  return new Promise((resolve) => {
    // Check if element already exists
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        clearInterval(interval);
        resolve(element);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        resolve(null);
      }
    }, 100);
  });
};

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const adminDetails = useAdminDetails();
  const userId = adminDetails?.id ? String(adminDetails.id) : undefined;
  const navigate = useNavigate();
  const location = useLocation();

  const [hasSeenTour, setHasSeenTour] = useState(true);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);

  // Track the route we navigated from (for back navigation)
  const navigationHistoryRef = useRef<string[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = getTourStorageKey(userId);
    const seen = localStorage.getItem(key) === "true";
    setHasSeenTour(seen);
  }, [userId]);

  const tourSteps = useMemo(() => getTourSteps(adminDetails), [adminDetails]);
  const steps = useMemo(() => toJoyrideSteps(tourSteps), [tourSteps]);

  useEffect(() => {
    if (!hasSeenTour && userId) {
      const timer = setTimeout(() => setIsTourRunning(true), 500);
      return () => clearTimeout(timer);
    }
  }, [hasSeenTour, userId]);

  // Prevent horizontal scroll during tour
  useEffect(() => {
    if (isTourRunning) {
      // Save original styles
      const originalHtmlOverflow = document.documentElement.style.overflowX;
      const originalBodyOverflow = document.body.style.overflowX;

      // Prevent horizontal scroll
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowX = "hidden";

      // Reset any existing horizontal scroll
      window.scrollTo(0, window.scrollY);

      return () => {
        // Restore original styles
        document.documentElement.style.overflowX = originalHtmlOverflow;
        document.body.style.overflowX = originalBodyOverflow;
      };
    }
  }, [isTourRunning]);

  // Track navigation history when tour is running
  useEffect(() => {
    if (isTourRunning && !isNavigating) {
      const currentPath = location.pathname;
      const history = navigationHistoryRef.current;

      // Only add if different from last entry
      if (history.length === 0 || history[history.length - 1] !== currentPath) {
        navigationHistoryRef.current = [...history, currentPath];
      }
    }
  }, [location.pathname, isTourRunning, isNavigating]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsTourRunning(true);
    navigationHistoryRef.current = [location.pathname];
  }, [location.pathname]);

  const endTour = useCallback(() => {
    setIsTourRunning(false);
    setIsNavigating(false);
    if (typeof window !== "undefined" && userId) {
      localStorage.setItem(getTourStorageKey(userId), "true");
    }
    setHasSeenTour(true);
    navigationHistoryRef.current = [];
  }, [userId]);

  /**
   * Navigate to a step's route if needed
   * Returns true if navigation was triggered, false if no navigation needed
   */
  const navigateToStep = useCallback(
    async (targetStepIndex: number): Promise<boolean> => {
      const step = tourSteps[targetStepIndex];
      if (!step) return false;

      const stepRoute = step.route;
      const currentPath = location.pathname;

      // Check if we need to navigate
      const needsNavigation = stepRoute && stepRoute !== currentPath;

      if (!needsNavigation) {
        // No navigation needed, but still wait for element if specified
        const waitSelector =
          step.waitForSelector || (step.target !== "body" ? step.target : null);
        if (waitSelector) {
          const timeout = step.waitTimeout || DEFAULT_WAIT_TIMEOUT;
          await waitForElement(waitSelector, timeout);
        }
        return false;
      }

      // Navigate to the step's route
      setIsNavigating(true);
      navigate(stepRoute);

      // Wait for the target element to appear
      const waitSelector =
        step.waitForSelector || (step.target !== "body" ? step.target : null);
      if (waitSelector) {
        const timeout = step.waitTimeout || DEFAULT_WAIT_TIMEOUT;
        await waitForElement(waitSelector, timeout);
        // Additional delay for React to fully commit DOM changes
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else {
        // Small delay to let the page render
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      setIsNavigating(false);
      return true;
    },
    [tourSteps, location.pathname, navigate]
  );

  const currentRoute = location.pathname;

  return (
    <TourContext.Provider
      value={{
        isTourRunning,
        stepIndex,
        setStepIndex,
        startTour,
        endTour,
        hasSeenTour,
        steps,
        tourSteps,
        isNavigating,
        navigateToStep,
        currentRoute,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error("useTour must be used within TourProvider");
  return context;
};
